import React, {useCallback, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Checkbox, Typography, Space, Spin, Divider} from 'antd';
import {DownloadOutlined} from '@ant-design/icons';
import {tid} from '../../../../../testUtils';
import {SideBarModalProps} from '../../../../layout/common/SideBarModal';
import {modelDownloadAll, modelDownloadAllWithFilters} from '../../../../../store/model';
import {useMounted} from '../../../../../hooks/useMounted';
import {ApiResponse, GetModelsForDynamicFiltersRequest} from 'hemwb-api';
import {downloadFromResponse} from '../../../../../utils/download';
import {ModelFilterState} from '../../filter';

type ModelDownloadAllSideBarModalAntdProps = SideBarModalProps & {
  onSuccess?: () => void;
  filterState?: ModelFilterState;
  searchValue?: string;
};

const ModelDownloadAllSideBarModalAntd: React.FC<ModelDownloadAllSideBarModalAntdProps> = ({
  closeModal,
  onSuccess,
  filterState,
  searchValue,
}) => {
  const dispatch = useDispatch();
  const mounted = useMounted();
  const [submitting, setSubmitting] = useState(false);
  const [includeHistory, setIncludeHistory] = useState(false);
  const cancelled = useRef(false);

  const downloadIfNotCancelled = useCallback(
    (response: ApiResponse<Blob>) => (cancelled.current ? Promise.reject() : downloadFromResponse(response.raw)),
    [],
  );

  const handleSubmit = useCallback(() => {
    setSubmitting(true);

    // If filters are provided, use the new POST API with filters
    if (filterState || searchValue) {
      // Pass the filterState directly - the API will transform it to the correct format
      const requestFilters: GetModelsForDynamicFiltersRequest = {
        ...filterState,
        search: searchValue || filterState?.search,
      } as GetModelsForDynamicFiltersRequest;

      return modelDownloadAllWithFilters(dispatch, requestFilters)
        .then(downloadIfNotCancelled)
        .then(() => {
          onSuccess?.();
          closeModal();
        })
        .catch(() => null)
        .finally(() => {
          mounted.current && setSubmitting(false);
        });
    } else {
      // Fall back to old API if no filters
      return modelDownloadAll(dispatch, includeHistory)
        .then(downloadIfNotCancelled)
        .then(() => {
          onSuccess?.();
          closeModal();
        })
        .catch(() => null)
        .finally(() => {
          mounted.current && setSubmitting(false);
        });
    }
  }, [dispatch, downloadIfNotCancelled, mounted, onSuccess, closeModal, includeHistory, filterState, searchValue]);

  const handleClose = useCallback(() => {
    cancelled.current = true;
    closeModal();
  }, [closeModal]);

  return (
    <div style={{padding: '24px', width: '100%'}}>
      <Spin spinning={submitting}>
        <Space direction="vertical" style={{width: '100%'}} size="large">
          {/* Header */}
          <div>
            <Typography.Title level={4} style={{margin: 0, display: 'flex', alignItems: 'center', gap: '8px'}}>
              <DownloadOutlined />
              Download All Entries
            </Typography.Title>
            <Typography.Text type="secondary" style={{fontSize: '12px'}}>
              This operation can take a long time
            </Typography.Text>
          </div>

          <Divider style={{margin: '12px 0'}} />

          {/* Checkbox */}
          {!filterState && !searchValue && (
            <Checkbox
              checked={includeHistory}
              onChange={(e) => setIncludeHistory(e.target.checked)}
              style={{marginBottom: '16px'}}
            >
              <Typography.Text>
                Include Items history
                <Typography.Text type="secondary" style={{fontSize: '12px', marginLeft: '4px'}}>
                  (This will significantly increase downloading time.)
                </Typography.Text>
              </Typography.Text>
            </Checkbox>
          )}

          {/* Buttons */}
          <Space style={{width: '100%', justifyContent: 'flex-end', marginTop: '24px'}}>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleSubmit}
              loading={submitting}
              {...tid('btn-download-all')}
            >
              Download All Entries
            </Button>
          </Space>
        </Space>
      </Spin>
    </div>
  );
};

export default ModelDownloadAllSideBarModalAntd;
